'use client';

/**
 * Account Registration Component
 *
 * Handles user registration, validates input, displays appropriate error messages,
 * and logs the user in automatically upon success.
 *
 * @param {Function} onRegisterSuccess - Optional callback executed after successful registration.
 * @returns {JSX.Element} The registration component.
 */

// React & Core Hook Imports
import React, { ReactNode, useState } from 'react';

// Third-Party Library Imports
import { useRouter } from 'next/navigation';
import { useForm, useWatch, SubmitHandler, FieldErrors, RegisterOptions } from 'react-hook-form';
import Link from 'next/link';
import { signIn } from 'next-auth/react';
import { v4 as uuidv4 } from 'uuid';
import { Eye, EyeOff } from 'lucide-react';

// Component Imports
import Toast from '@/components/toast';
import SubmitButton from '@/components/submitButton';
import CheckBox from '@/components/checkbox';

// Action Imports
import { RegisterUser } from '@/lib/functions/registerUser';

// Constants Imports
import { REGEX } from '@/lib/constants/common';

// Style Imports
import styles from './accountRegistration.module.scss';

const LABELS = {
  FIRST_NAME: 'First Name',
  LAST_NAME: 'Last Name',
  EMAIL: 'Email Address',
  PASSWORD: 'Password',
  CONFIRM_PASSWORD: 'Confirm Password',
  TERMS: (
    <p>I agree to the <a href="/legal/terms-conditions/" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>.</p>
  ),
};

// Form Fields Configuration
const FORM_FIELDS: Array<{
  id: keyof Inputs;
  label: ReactNode;
  errorMessage: string;
  registerOptions: RegisterOptions<Inputs, keyof Inputs>;
  type: string;
  autocomplete: string;
}> = [
  {
    id: 'register_firstname',
    label: LABELS.FIRST_NAME,
    errorMessage: 'Please enter a valid first name.',
    registerOptions: {
      required: true,
      pattern: REGEX.NAME_LATIN_1,
    },
    type: 'text',
    autocomplete: 'given-name',
  },
  {
    id: 'register_lastname',
    label: LABELS.LAST_NAME,
    errorMessage: 'Please enter a valid last name.',
    registerOptions: {
      required: true,
      pattern: REGEX.NAME_LATIN_1,
    },
    type: 'text',
    autocomplete: 'family-name',
  },
  {
    id: 'register_email',
    label: LABELS.EMAIL,
    errorMessage: 'Please enter a valid email address.',
    registerOptions: {
      required: true,
      pattern: REGEX.NEW_EMAIL,
    },
    type: 'email',
    autocomplete: 'email',
  },
  {
    id: 'register_password',
    label: LABELS.PASSWORD,
    errorMessage: 'Passwords must be at least 8 characters long, and include at least one uppercase, one lowercase, one number, and one special character.',
    registerOptions: {
      required: true,
      pattern: REGEX.PASSWORD,
    },
    type: 'password',
    autocomplete: 'new-password',
  },
  {
    id: 'register_confirm_password',
    label: LABELS.CONFIRM_PASSWORD,
    errorMessage: 'Passwords do not match.',
    registerOptions: {
      required: 'Please confirm your password.',
    },
    type: 'password',
    autocomplete: 'off',
  },
];

// Form Input Type Definition
type Inputs = {
  register_firstname: string;
  register_lastname: string;
  register_password: string;
  register_confirm_password: string;
  register_email: string;
  agreeToTerms: boolean;
};

interface AccountRegistrationProps {
  onRegisterSuccess?: () => void; // Optional callback executed after successful registration
}

export default function AccountRegistration({ onRegisterSuccess }: AccountRegistrationProps) {
  const router = useRouter();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [submitStep, setSubmitStep] = useState('Registering');

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, submitCount },
    trigger,
    clearErrors,
  } = useForm<Inputs>();

  // Watch each form field individually
  const firstName = useWatch({ control, name: 'register_firstname', defaultValue: '' });
  const lastName = useWatch({ control, name: 'register_lastname', defaultValue: '' });
  const email = useWatch({ control, name: 'register_email', defaultValue: '' });
  const password = useWatch({ control, name: 'register_password', defaultValue: '' });
  const confirmPassword = useWatch({ control, name: 'register_confirm_password', defaultValue: '' });

  // Check if all fields are filled
  const allFieldsFilled = !!firstName && !!lastName && !!email && !!password;
  const confirmPasswordFilled = !!confirmPassword;

  // Dynamically check if form is valid
  const isFormValid = allFieldsFilled && confirmPasswordFilled && agreeToTerms && Object.keys(errors).length === 0;

  /**
   * Validates form fields and sets error messages accordingly.
   */
  const validateFields = (errors: FieldErrors<Inputs>) => {
    const missing = FORM_FIELDS
      .filter(({ id }) => errors[id]?.type === 'required')
      .map(({ label }) => label);

    const invalid = FORM_FIELDS
      .filter(({ id }) => errors[id] && errors[id]?.type !== 'required')
      .map(({ label }) => label);

    if (!agreeToTerms) {
      missing.push(LABELS.TERMS);
    }

    if (missing.length) setErrorMessage('Please enter all required fields.');
    if (invalid.length) setErrorMessage(`The following fields are invalid: ${invalid.join(', ')}`);
  };

  /**
   * Handles registration form submission.
   */
  const onRegister: SubmitHandler<Inputs> = async (data) => {
    clearErrors();
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!agreeToTerms) {
      setErrorMessage('You must agree to the Terms and Conditions.');
      return;
    }

    try {
      setSubmitStep('Registering');

      const response = await RegisterUser(
        data.register_email,
        data.register_firstname,
        data.register_lastname,
        data.register_password
      );

      setSubmitStep('Completing');

      if (!response.success) {
        if (response.errors) {
          setErrorMessage(`The following fields are invalid: ${Object.values(response.errors).join(', ')}`);
        } else if (response.message) {
          setErrorMessage(response.message.includes('Taken') ? 'This email is already taken.' : response.message);
        } else {
          setErrorMessage('Your account could not be created.');
        }
        return;
      }

      setSubmitStep('Signing in');

      const loginResponse = await signIn('credentials', {
        email: data.register_email,
        password: data.register_password,
        redirect: false,
      });

      if (loginResponse?.error) {
        setErrorMessage('Registration succeeded, but login failed.');
      } else {
        if (onRegisterSuccess) {
          onRegisterSuccess();
        } else {
          router.refresh();
        }
      }
    } catch {
      setErrorMessage('Your account could not be created.');
    }
  };

  return (
    <div className={styles.wrapper}>
      {!successMessage && (
        <form onSubmit={handleSubmit(onRegister, validateFields)} noValidate>
          <div className={styles.formColumns}>
            {FORM_FIELDS.slice(0, 2).map(({ id, label, errorMessage, registerOptions, type, autocomplete }) => (
              <div className="formInput formText" key={id}>
                <label htmlFor={id}>
                  {label} {registerOptions.required && <span className={styles.required}>*</span>}
                </label>
                <input
                  id={id}
                  type={type}
                  aria-invalid={errors[id] ? 'true' : 'false'}
                  aria-describedby={errors[id] ? `${id}-error` : undefined}
                  {...register(id, registerOptions)}
                  disabled={isSubmitting}
                  autoComplete={autocomplete}
                />
                {errors[id] && errorMessage && (
                  <span id={`${id}-error`} className={styles.inlineValidationError}>
                    {errorMessage}
                  </span>
                )}
              </div>
            ))}
          </div>

          {FORM_FIELDS.slice(2).map(({ id, label, errorMessage, registerOptions, type, autocomplete }) => (
            <div className="formInput formText" key={id}>
              <label htmlFor={id}>{label} {registerOptions.required && <span className={styles.required}>*</span>}</label>
              <div className={styles.passwordWrapper}>
                <input
                  id={id}
                  type={
                    id === 'register_password'
                      ? (showPassword ? 'text' : 'password')
                      : (id === 'register_confirm_password'
                        ? (showConfirmPassword ? 'text' : 'password')
                        : type)
                  }
                  {...register(
                    id,
                    id === 'register_confirm_password'
                      ? {
                        required: 'Please confirm your password.',
                        validate: (value) =>
                          value === password || 'Passwords do not match.',
                        onChange: () => (submitCount > 0) && trigger('register_confirm_password'),
                      }
                      : id === 'register_password'
                        ? {
                          required: true,
                          pattern: REGEX.PASSWORD,
                          onChange: () => (submitCount > 0) && trigger('register_confirm_password'),
                        }
                        : registerOptions
                  )}
                  disabled={isSubmitting}
                  autoComplete={autocomplete}
                  aria-invalid={errors[id] ? 'true' : 'false'}
                  aria-describedby={errors[id] ? `${id}-error` : undefined}
                />
                {id === 'register_password' && (
                  <button
                    type="button"
                    className={styles.toggleVisibility}
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                )}

                {id === 'register_confirm_password' && (
                  <button
                    type="button"
                    className={styles.toggleVisibility}
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    {showConfirmPassword ? <EyeOff /> : <Eye />}
                  </button>
                )}
              </div>
              {errors[id] && errorMessage && (
                <span id={`${id}-error`} className={styles.inlineValidationError}>
                  {errorMessage}
                </span>
              )}
            </div>
          ))}

          <div className={styles.termsContainer}>
            <CheckBox
              key="agree-to-terms"
              id="agreeToTerms"
              label={<>{LABELS.TERMS}</>}
              data={{ parameter: 'terms', value: 'exclusives-agree' }}
              isChecked={() => agreeToTerms}
              onCheck={() => setAgreeToTerms(!agreeToTerms)}
              required
              disabled={isSubmitting}
            />
          </div>

          <div className={styles.privacyPolicy}>
            <p>
              Your personal data will be used to support your experience throughout this website, to manage access to your account, and for other purposes described in our{' '}
              <Link href="/legal/privacy-policy/" target="_blank">privacy policy</Link>.
            </p>
          </div>

          {isSubmitting && (
            <p className={styles.submitStatus} role="status">
              Just a moment, your account is getting set up.
            </p>
          )}

          <div>
            <SubmitButton
              label="Register"
              loadingLabel={submitStep}
              loading={isSubmitting}
              disabled={!isFormValid}
            />
          </div>
        </form>
      )}
      <div role="alert" aria-atomic="true">
        {errorMessage && <Toast id={uuidv4()} type="error" label={true} message={errorMessage} />}
      </div>
      <div aria-live="polite">
        {successMessage && <Toast id={uuidv4()} type="success" label={true} message={successMessage} />}
      </div>
    </div>
  );
}
